<script setup lang="ts">
  export interface HorizontalBarItem {
    label: string;
    value: number;
  }

  const MIN_BAR_WIDTH_PX = 4;
  import { computed } from 'vue';
  import { formatNumber } from '../model/format';

  interface Props {
    items: readonly HorizontalBarItem[];
    maxHeight?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    maxHeight: undefined,
  });

  const sortedItems = computed(() =>
    [...props.items].sort((first, second) => second.value - first.value)
  );

  const maxValue = computed(() => Math.max(0, ...props.items.map((item) => item.value)));

  const valueReserve = computed(() => {
    const maxChars = Math.max(0, ...props.items.map((item) => formatNumber(item.value).length));
    return `calc(${maxChars}ch + 0.5rem)`;
  });

  const ariaLabel = computed(() =>
    sortedItems.value.map((item) => `${item.label}: ${formatNumber(item.value)}`).join('; ')
  );

  function barWidth(value: number): string {
    const ratio = maxValue.value > 0 ? Math.min(Math.max(value / maxValue.value, 0), 1) : 0;
    if (ratio <= 0) return `${MIN_BAR_WIDTH_PX}px`;
    return `calc((100% - ${valueReserve.value}) * ${ratio})`;
  }

  function round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  function barColor(value: number): string {
    const ratio = maxValue.value > 0 ? Math.min(Math.max(value / maxValue.value, 0), 1) : 0;
    const progress = 1 - ratio;
    if (progress <= 0) return 'var(--chart-1)';
    if (progress <= 0.5) {
      return `color-mix(in oklch, var(--chart-3) ${round(progress * 200)}%, var(--chart-1))`;
    }
    return `color-mix(in oklch, var(--chart-4) ${round((progress - 0.5) * 200)}%, var(--chart-3))`;
  }
</script>

<template>
  <div
    data-testid="horizontal-bar-list"
    role="img"
    :aria-label="ariaLabel"
    class="overflow-y-auto"
    :style="props.maxHeight !== undefined ? { maxHeight: `${props.maxHeight}px` } : undefined"
  >
    <div class="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-7 gap-y-2">
      <template
        v-for="item in sortedItems"
        :key="item.label"
      >
        <span
          data-testid="horizontal-bar-label"
          class="text-right text-xs text-gray-03"
        >
          {{ item.label }}
        </span>
        <div class="flex min-w-0 items-center">
          <div
            data-testid="horizontal-bar"
            class="bar-grow-x h-[31px] shrink-0 rounded-lg"
            :style="{ width: barWidth(item.value), '--bar-color': barColor(item.value), backgroundColor: 'var(--bar-color)' }"
          />
          <span
            data-testid="horizontal-bar-value"
            class="ml-2 shrink-0 whitespace-nowrap text-xs text-gray-07"
          >
            {{ formatNumber(item.value) }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
  @media (prefers-reduced-motion: no-preference) {
    .bar-grow-x {
      transition:
        width 400ms ease-out,
        background-color 400ms ease-out;
      animation: bar-grow-x 400ms ease-out;
    }

    @keyframes bar-grow-x {
      from {
        width: 0;
      }
    }
  }
</style>
